import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Bell, MessageCircle, Heart, Users, Package, Award, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const PREFERENCE_ITEMS = [
  {
    key: 'notify_comment_reply',
    icon: MessageCircle,
    label: 'Respuestas a comentarios',
    description: 'Cuando alguien responda a tus comentarios',
  },
  {
    key: 'notify_post_like',
    icon: Heart,
    label: 'Likes en posts',
    description: 'Cuando alguien le dé like a tus publicaciones',
  },
  {
    key: 'notify_new_posts',
    icon: Users,
    label: 'Nuevos posts en grupos',
    description: 'Cuando haya nuevos posts en grupos que sigues',
  },
  {
    key: 'notify_product_updates',
    icon: Package,
    label: 'Actualizaciones de productos',
    description: 'Cuando haya cambios en productos que escaneaste',
  },
  {
    key: 'notify_health_reminders',
    icon: Bell,
    label: 'Recordatorios de salud',
    description: 'Recordatorios personalizados según tu perfil',
  },
  {
    key: 'notify_milestones',
    icon: Award,
    label: 'Logros y hitos',
    description: 'Cuando alcances nuevos logros y recompensas',
  },
];

export default function NotificationSettings({ user, onClose }) {
  const queryClient = useQueryClient();
  const [preferences, setPreferences] = useState({
    notify_comment_reply: true,
    notify_post_like: true,
    notify_new_posts: true,
    notify_product_updates: true,
    notify_health_reminders: true,
    notify_milestones: true,
    quiet_hours_start: '',
    quiet_hours_end: '',
  });

  const { data: existingPrefs } = useQuery({
    queryKey: ['notification-preferences', user?.id],
    queryFn: async () => {
      const prefs = await base44.entities.NotificationPreference.filter({ user_id: user?.id });
      return prefs[0] || null;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (existingPrefs) {
      setPreferences({
        notify_comment_reply: existingPrefs.notify_comment_reply ?? true,
        notify_post_like: existingPrefs.notify_post_like ?? true,
        notify_new_posts: existingPrefs.notify_new_posts ?? true,
        notify_product_updates: existingPrefs.notify_product_updates ?? true,
        notify_health_reminders: existingPrefs.notify_health_reminders ?? true,
        notify_milestones: existingPrefs.notify_milestones ?? true,
        quiet_hours_start: existingPrefs.quiet_hours_start || '',
        quiet_hours_end: existingPrefs.quiet_hours_end || '',
      });
    }
  }, [existingPrefs]);

  const savePreferencesMutation = useMutation({
    mutationFn: async (prefs) => {
      if (existingPrefs) {
        return await base44.entities.NotificationPreference.update(existingPrefs.id, prefs);
      } else {
        return await base44.entities.NotificationPreference.create({
          user_id: user.id,
          ...prefs,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success('Preferencias guardadas');
      onClose();
    },
  });

  const handleSave = () => {
    savePreferencesMutation.mutate(preferences);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Configuración de Notificaciones</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5 text-gray-600" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Push Notification Info */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
            <div className="flex gap-3">
              <Bell className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900 text-sm">Notificaciones Push</p>
                <p className="text-xs text-blue-700 mt-1">
                  Para recibir notificaciones cuando no estés usando la app, necesitamos habilitar notificaciones push. 
                  Esta función estará disponible próximamente.
                </p>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="space-y-4">
            {PREFERENCE_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.key} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{item.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                  </div>
                  <Switch
                    checked={preferences[item.key]}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, [item.key]: checked })}
                  />
                </div>
              );
            })}
          </div>

          {/* Quiet Hours */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Modo Silencio</h3>
            </div>
            <p className="text-sm text-gray-600">No recibir notificaciones durante estas horas</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Desde</label>
                <Input
                  type="time"
                  value={preferences.quiet_hours_start}
                  onChange={(e) => setPreferences({ ...preferences, quiet_hours_start: e.target.value })}
                  className="h-10"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Hasta</label>
                <Input
                  type="time"
                  value={preferences.quiet_hours_end}
                  onChange={(e) => setPreferences({ ...preferences, quiet_hours_end: e.target.value })}
                  className="h-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancelar
          </Button>
          <Button onClick={handleSave} className="flex-1 bg-blue-600 hover:bg-blue-700">
            Guardar
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}